import { useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { REPEATS, WEEKDAYS, occurrences } from '../../data/recurrence';
import { formatDateRange, newId } from '../../data/model';
import { ALL_TERMS } from '../../data/selectors';
import './AddRecurringDialog.css';

const NEW_FOLDER = '__new__';

/**
 * Creates a term's worth of a weekly session in one step.
 *
 * This is the shape of every check-in block in the sheets this replaces: one
 * weekday, repeated for a semester. Typed by hand each August, fifteen dates at
 * a time, per session, per semester.
 */
function AddRecurringDialog({ table, dispatch, activeTermId, onAdded, onClose }) {
  const activeTerm = table.terms.find((term) => term.id === activeTermId);

  const [form, setForm] = useState(() => ({
    name: '',
    repeats: 'weekly',
    weekday: 1,
    startDate: activeTerm?.startDate || '',
    endDate: activeTerm?.endDate || '',
    weight: '1',
    folderId: NEW_FOLDER,
    newFolderName: '',
    folderGroupId: '',
    folderParentId: '',
    termId: activeTermId === ALL_TERMS ? activeTerm?.id || '' : activeTermId || '',
  }));

  const set = (changes) => setForm((current) => ({ ...current, ...changes }));
  const [skipped, setSkipped] = useState(() => new Set());
  // Ids are drawn from a pool kept for the life of the dialog, so a second
  // press of Add before it closes asks for the same series and is dropped.
  const idPool = useRef({ events: [], folder: newId('f') });
  const idsFor = (count) => {
    const pool = idPool.current.events;
    while (pool.length < count) pool.push(newId('e'));
    return pool.slice(0, count);
  };

  const allDates = useMemo(
    () =>
      occurrences({
        repeats: form.repeats,
        startDate: form.startDate,
        endDate: form.endDate,
        weekday: form.weekday,
      }),
    [form.repeats, form.startDate, form.endDate, form.weekday]
  );
  // A term runs through a holiday and finals week. CIR's first series ran to
  // the last day of term and they marked the dates that never happened as
  // excused, one cell at a time; leaving them out here is one click each.
  const dates = useMemo(() => allDates.filter((date) => !skipped.has(date)), [allDates, skipped]);

  const toggleDate = (date) =>
    setSkipped((current) => {
      const next = new Set(current);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });

  const creatingFolder = form.folderId === NEW_FOLDER;
  const folderName = creatingFolder ? form.newFolderName.trim() : '';
  const existingFolder = creatingFolder ? null : table.folders.find((folder) => folder.id === form.folderId);

  // Every date is shown under its folder, so a session name that only repeats
  // the folder's adds nothing. It can be left blank.
  const sessionName = form.name.trim() || folderName || existingFolder?.name || '';
  const canSubmit = sessionName && dates.length > 0 && (!creatingFolder || folderName);

  // A group that already has a weekly series would get a second set of dates
  // for the same weeks. That is how CIR's Friday students ended up in two
  // folders with two different records of the same four sessions.
  const cohortGroup = creatingFolder ? table.groups.find((group) => group.id === form.folderGroupId) : null;
  const cohortFolders = cohortGroup
    ? table.folders.filter((folder) => folder.groupId === cohortGroup.id)
    : [];

  // A new folder named like one that exists is usually the existing one wanted
  // back — with the wrong group attending it.
  const sameName = creatingFolder && folderName
    ? table.folders.find((folder) => folder.name.trim().toLowerCase() === folderName.toLowerCase())
    : null;
  const sameNameGroup = sameName && table.groups.find((group) => group.id === sameName.groupId);

  // Naming the folder after the session is what people almost always want, so
  // it is offered rather than demanded.
  const suggestFolder = () => {
    if (creatingFolder && !form.newFolderName && form.name.trim()) set({ newFolderName: form.name.trim() });
  };

  const submit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const ids = idsFor(dates.length);
    const newFolderId = creatingFolder ? idPool.current.folder : null;
    const folderId = creatingFolder ? null : form.folderId || null;
    dispatch({
      type: 'events/addRecurring',
      ids,
      name: sessionName,
      repeats: form.repeats,
      weekday: form.weekday,
      startDate: form.startDate,
      endDate: form.endDate,
      skipDates: allDates.filter((date) => skipped.has(date)),
      weight: form.weight,
      folderId,
      newFolderId,
      newFolderName: creatingFolder ? folderName : null,
      folderGroupId: creatingFolder ? form.folderGroupId || null : null,
      folderParentId: creatingFolder ? form.folderParentId || null : null,
      termId: form.termId || null,
    });
    onAdded?.({
      eventId: ids[0],
      folderId: newFolderId || folderId,
      newFolder: newFolderId ? { id: newFolderId, parentId: form.folderParentId || null } : null,
      termId: form.termId || null,
    });
    onClose();
  };

  return (
    <Modal
      title="Add a repeating session"
      description="One session, repeated for the whole term."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="add-recurring" className="btn btn--primary" disabled={!canSubmit}>
            Add {dates.length || ''} {dates.length === 1 ? 'session' : 'sessions'}
          </button>
        </>
      }
    >
      <form id="add-recurring" onSubmit={submit} className="stack">
        <div className="row">
          <label className="field">
            <span>Session name</span>
            <input
              className="input"
              autoFocus
              placeholder={folderName || existingFolder?.name || 'Monday 2pm'}
              value={form.name}
              onChange={(event) => set({ name: event.target.value })}
              onBlur={suggestFolder}
            />
          </label>
          <label className="field">
            <span>Repeats</span>
            <select
              className="select"
              value={form.repeats}
              onChange={(event) => set({ repeats: event.target.value })}
            >
              {REPEATS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Day</span>
            <select
              className="select"
              value={form.weekday}
              onChange={(event) => set({ weekday: Number(event.target.value) })}
            >
              {WEEKDAYS.map((day) => (
                <option key={day.value} value={day.value}>{day.label}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span>From</span>
            <input
              className="input"
              type="date"
              value={form.startDate}
              onChange={(event) => set({ startDate: event.target.value })}
            />
          </label>
          <label className="field">
            <span>Until</span>
            <input
              className="input"
              type="date"
              min={form.startDate || undefined}
              value={form.endDate}
              onChange={(event) => set({ endDate: event.target.value })}
            />
          </label>
          <label className="field field--narrow">
            <span>Weight</span>
            <input
              className="input"
              type="number"
              min="0"
              step="0.5"
              value={form.weight}
              onChange={(event) => set({ weight: event.target.value })}
            />
          </label>
        </div>

        <div className="row">
          <label className="field">
            <span>Term</span>
            <select className="select" value={form.termId} onChange={(event) => set({ termId: event.target.value })}>
              <option value="">No term</option>
              {table.terms.map((term) => (
                <option key={term.id} value={term.id}>{term.name}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Folder</span>
            <select
              className="select"
              value={form.folderId}
              onChange={(event) => set({ folderId: event.target.value })}
            >
              <option value="">No folder</option>
              {table.folders.map((folder) => (
                <option key={folder.id} value={folder.id}>{folder.name}</option>
              ))}
              <option value={NEW_FOLDER}>New folder…</option>
            </select>
          </label>
          {creatingFolder && (
            <label className="field">
              <span>New folder name</span>
              <input
                className="input"
                placeholder={form.name.trim() || 'Monday 2pm'}
                value={form.newFolderName}
                onChange={(event) => set({ newFolderName: event.target.value })}
              />
            </label>
          )}
        </div>

        {creatingFolder && (
          <label className="field">
            <span>Section</span>
            <select
              className="select"
              value={form.folderParentId}
              onChange={(event) => set({ folderParentId: event.target.value })}
            >
              <option value="">Top level</option>
              {table.folders
                .filter((folder) => !folder.parentId)
                .map((folder) => (
                  <option key={folder.id} value={folder.id}>{folder.name}</option>
                ))}
            </select>
          </label>
        )}

        {creatingFolder && (
          <label className="field">
            <span>Who attends</span>
            <select
              className="select"
              value={form.folderGroupId}
              onChange={(event) => {
                const group = table.groups.find((entry) => entry.id === event.target.value);
                // A weekly folder is almost always named after the group in it.
                set(
                  group && !form.newFolderName.trim()
                    ? { folderGroupId: event.target.value, newFolderName: group.name }
                    : { folderGroupId: event.target.value }
                );
              }}
            >
              <option value="">Everyone</option>
              {table.groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name} ({group.memberIds.length})
                </option>
              ))}
            </select>
          </label>
        )}

        {creatingFolder && cohortFolders.length > 0 && (
          <p className="warning-text">
            {cohortGroup.name} already attends {cohortFolders.map((folder) => folder.name).join(' and ')}.
            A second series gives them two sets of dates for the same weeks. To add dates to the
            existing one, choose it under Folder instead.
          </p>
        )}

        {sameName && !cohortFolders.includes(sameName) && (
          <p className="warning-text">
            There is already a folder called {sameName.name}
            {sameNameGroup ? `, attended by ${sameNameGroup.name}` : ''}. To use it instead, choose it
            under Folder — and right-click its header to change who attends it.
          </p>
        )}

        {creatingFolder && cohortFolders.length === 0 && !sameName && (
          <p className="hint">
            Naming a group here keeps the grid readable: only those people get a cell under these
            sessions, and only they are scored on them. Leave it as Everyone for a one-off open to
            the whole programme.
          </p>
        )}

        {allDates.length > 0 ? (
          <>
            <p className="hint">
              {dates.length} {dates.length === 1 ? 'session' : 'sessions'}
              {dates.length > 0 && `, ${formatDateRange(dates[0], dates[dates.length - 1])}`} —{' '}
              {form.repeats === 'weekly'
                ? `every ${WEEKDAYS.find((day) => day.value === form.weekday)?.label}`
                : `the ${form.repeats === 'monthly-last' ? 'last' : 'first'} ${
                    WEEKDAYS.find((day) => day.value === form.weekday)?.label
                  } of each month`}
              . Click a date to leave it out — a holiday, or finals week.
            </p>
            <div className="recurring-dates" role="group" aria-label="Dates">
              {allDates.map((date) => {
                const off = skipped.has(date);
                return (
                  <button
                    key={date}
                    type="button"
                    className={`recurring-dates__date${off ? ' is-off' : ''}`}
                    aria-pressed={!off}
                    title={off ? 'Left out — click to put back' : 'Click to leave out'}
                    onClick={() => toggleDate(date)}
                  >
                    {formatDateRange(date)}
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <p className="hint">Pick a date range that contains at least one such day.</p>
        )}
      </form>
    </Modal>
  );
}

export default AddRecurringDialog;
